import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Hook to wait for LaunchDarkly flags to be ready.
 *
 * This hook checks for the presence of known flags to determine when LaunchDarkly
 * has finished loading, handles all logging internally, and returns a simple boolean.
 *
 * Uses sensible defaults:
 * - 5 second timeout
 * - Automatic logging to Financing.CustomerPortal2 category
 * - Detects common financing flags or any loaded flags
 *
 * @returns boolean indicating whether LaunchDarkly is ready
 *
 * @example
 * ```typescript
 * const isLdReady = useLaunchDarklyReady();
 *
 * if (!isLdReady) {
 *   return <LoadingScreen message="Loading feature flags..." />;
 * }
 * ```
 */
export const useLaunchDarklyReady = (): boolean => {
    const [isReady, setIsReady] = useState(false);
    const flags = useFlags();

    useEffect(() => {
        const hasLoadedFlags = Object.keys(flags).length > 0;

        if (hasLoadedFlags && !isReady) {
            setIsReady(true);
            return;
        }

        // Fallback timeout: don't wait forever for LaunchDarkly (5 seconds)
        const fallbackTimeout = setTimeout(() => {
            if (!isReady) {
                setIsReady(true);
            }
        }, 5000);

        return () => clearTimeout(fallbackTimeout);
    }, [flags, isReady]);

    return isReady;
};
